import { z } from 'zod';
import { LedgerKindSchema } from '../ledger.js';
import { createAggregateQuerySchema, createAggregateResponseSchema } from './aggregate.js';
import { CalendarDateOnlySchema, LowercaseUuidSchema } from './common.js';
import { DocumentPaymentStatusSchema, PaymentDirectionSchema, PaymentFundingSourceSchema } from './document-payment.js';
// =============================================================================
// Client payments: the filters and totals contract
// =============================================================================
//
// The shared filter shape for a client payment list and its totals sibling.
// Only the totals half exists so far (`aggregatePayments`); the list route
// extends `ClientPaymentAggregateFiltersQuerySchema` the way
// `InvoiceListQuerySchema` extends the invoice one, so the two cannot drift on
// what "the same filters" means.
/**
 * Every FILTER field shared by a client payment list and its totals.
 *
 * `paidFrom` / `paidTo` bound `Payment.paidAt`, the real-world value date, and
 * are inclusive. They are not the invoice filters' `dateFrom` / `dateTo`,
 * which bound a different date.
 */
export const ClientPaymentAggregateFiltersQuerySchema = z.object({
    clientId: LowercaseUuidSchema.optional(),
    direction: PaymentDirectionSchema.optional(),
    status: z.array(DocumentPaymentStatusSchema).min(1).optional(),
    fundingSource: z.array(PaymentFundingSourceSchema).min(1).optional(),
    ledgerKind: LedgerKindSchema.optional(),
    paidFrom: CalendarDateOnlySchema.optional(),
    paidTo: CalendarDateOnlySchema.optional(),
});
/** Dimensions `aggregatePayments` can group by. */
export const ClientPaymentAggregateDimensionSchema = z.enum(['client', 'fundingSource']);
export const ClientPaymentAggregateQuerySchema = createAggregateQuerySchema(ClientPaymentAggregateFiltersQuerySchema, ClientPaymentAggregateDimensionSchema);
export const ClientPaymentAggregateResponseSchema = createAggregateResponseSchema({
    // `count` is the number of distinct payments. Amounts carry one entry per
    // currency because totals in different currencies must never be added together.
    measures: z.object({
        amounts: z.array(z.object({
            currency: z.string(),
            // The full amount of each counted payment, so a total equals the sum of the list rows.
            amountMinorUnits: z.int(),
            // The part allocated to the filtered invoices; null when no invoice filter was applied.
            allocatedMinorUnits: z.int().nullable(),
        })),
    }),
    key: z.object({
        clientId: z.string().optional(),
        clientName: z.string().optional(),
        fundingSource: PaymentFundingSourceSchema.nullable().optional(),
    }),
    filter: z.object({
        clientId: z.string().nullable(),
        direction: PaymentDirectionSchema,
        status: z.array(DocumentPaymentStatusSchema),
        fundingSource: z.array(PaymentFundingSourceSchema),
        ledgerKind: LedgerKindSchema,
        paidFrom: z.string().nullable(),
        paidTo: z.string().nullable(),
        // The invoice ids themselves are service-only (one matter's ids would overflow a URL), so the echo is a count.
        invoiceIdCount: z.int().nullable(),
    }),
    dimension: ClientPaymentAggregateDimensionSchema,
});
